/** GET /api/hipage-offers — services/api/src/hipage-offers.ts. Read-only; EmmA's call is advice. */
export type JobPageLinkRow={id:string;job_ref:string;contact_id:string;created_by:string;created_at:string;expires_at:string;revoked_at:string|null;revoked_by:string|null;opens:number;first_opened_at:string|null;last_opened_at:string|null;status:'active'|'revoked'|'expired';current_choice:{kind:string;colour?:string;code?:string|null;product_name?:string;swatch_url?:string|null}|null;messages:number;last_event_at:string|null};
export type JobPageEventRow={id:number;link_id:string;kind:'open'|'choice'|'message'|'escalation'|'link';actor:string;detail:Record<string,unknown>;created_at:string};
export type EmmaCall = "yes" | "no" | "unsure" | "pending";
export type HipageOffer = {
  id: string; seen_at: string; trade: string | null; suburb?: string | null; postcode: string | null; credits: number | null;
  timing: string | null; action: string | null; description: string | null;
  emma: EmmaCall; emma_reason: string | null; emma_confidence: number | null; emma_at: string | null;
  /** Seconds from first seen to EmmA's call; late = over an hour (a catch-up). */
  emma_reaction_s: number | null; emma_late: boolean;
  verdict: EmmaCall | null; verdict_reason: string | null;
  /** EX GST. */
  potential_value: number | null; potential_basis: "trade" | "all"; potential_basis_jobs: number;
};
export type HipageOffersView = {
  days: number; since: string; auto_accept: false; offers: HipageOffer[];
  totals: { offers: number; yes: number; no: number; unsure: number; pending: number; potential_yes: number; credits_yes: number; reaction_median_s: number | null; reaction_on_time: number; reaction_late: number };
};
export type {InstallerStatements,InstallerPayments} from "@aiw/api";
export type MonthlyPerformance = {
  as_of:string;
  current:MonthlyPerformancePeriod;
  previous:MonthlyPerformancePeriod;
  monthly_revenue_target:number;
  fixed_cost_target:number;
  operating_profit:number|null;
  scheduled:ScheduledCoverage;
  /** @deprecated same figures as `scheduled`; kept for older app builds. */
  scheduled_next_30_days:ProfitCoverage;
  pending_schedule:WorkCoverage;
  opportunities_last_30_days:{from:string;through:string;value:number;gross_profit:number;profit_coverage_pct:number;lead_count:number;opportunity_count:number;priced_opportunity_count:number;profit_known_count:number;opportunities:OpportunityItem[]};
  build_up:BuildUpStep[];
};
export type CoverageJob={ref:string;name:string|null;stage:string|null;date:string|null;value:number;gross_profit:number|null;estimated?:boolean};
export type WorkCoverage={value:number;gross_profit:number|null;profit_coverage_pct:number|null;job_count:number;profit_known_count:number;jobs:CoverageJob[];estimated_count?:number};
export type ProfitCoverage=WorkCoverage&{from:string;through:string};
export type ScheduledCoverage=Omit<WorkCoverage,"jobs">&{date_passed_count:number;jobs:Array<CoverageJob&{date_passed:boolean}>};
export type BuildUpStep={key:"recognised"|"scheduled"|"pending_schedule"|"opportunity";revenue:number;gross_profit_known:number;unpriced_count:number;cumulative_revenue:number;cumulative_profit_known:number;gross_profit_estimated?:number};
export type OpportunityItem={id:string;ref:string|null;name:string;source:"aiw"|"legacy";stage:string;value:number|null;gross_profit:number|null;updated_at:string|null;proposal_id?:string;cost_unresolved?:number};
export type MonthlyPerformancePeriod = {
  month:string;
  revenue:number;
  gross_profit:number|null;
  job_count:number;
  profit_known_count:number;
  margin_pct:number|null;
  jobs:{id:string;ref:string;name:string|null;stage:string|null;close_date:string;revenue:number;gross_profit:number|null;source:"job"|"project";estimated?:boolean}[];
  estimated_count?:number;
};
// @aiw/api-client — the typed door. Every face talks to services/api through
// this and nothing else: reads come back as the user sees them (RLS), every
// mutation is a proposal, every case decision is a verdict. The face never
// names itself: the actor is attached by the server from the session.
import { hc } from "hono/client";
import type { CalendarData, AppType } from "@aiw/api";
import type { Case, Proposal, ProposalVerdict, ProposalResult, Surface, Verdict } from "@artemis-group/aiw-contracts";

export interface ApiClientOptions {
  baseUrl: string;
  /** Which face this is. Picks which membership the session exercises. */
  surface: Surface;
  /** Supabase session JWT — reads run as the user so RLS is real (ADR-002). */
  getToken?: () => Promise<string | null> | string | null;
  /** staff only: an explicit test set makes `test` rows visible (ADR-006) */
  testSetId?: () => string | null;
  fetch?: typeof fetch;
}

export type ApiClient = ReturnType<typeof hc<AppType>>;

export function createApiClient(opts: ApiClientOptions): ApiClient {
  return hc<AppType>(opts.baseUrl, {
    fetch: opts.fetch,
    headers: async () => {
      const token = (await opts.getToken?.()) ?? null;
      const h: Record<string, string> = { "x-aiw-surface": opts.surface };
      if (token) h.authorization = `Bearer ${token}`;
      const ts = opts.testSetId?.() ?? null;
      if (ts) h["x-aiw-test-set"] = ts;
      return h;
    },
  });
}

export class ApiError extends Error {
  constructor(readonly status: number, readonly body: unknown, message: string) {
    super(message);
  }
}

async function unwrap<T>(res: Response): Promise<T> {
  const text = await res.text();
  let body: unknown = null;
  try { body = text ? JSON.parse(text) : null; } catch { body = text; }
  if (!res.ok) {
    const e = body as { error?: string; reason?: string } | null;
    throw new ApiError(res.status, body, e?.error ?? e?.reason ?? `api ${res.status}`);
  }
  return body as T;
}

/** `{ ok: true, data }` read envelope → data */
async function unwrapData<T>(res: Response): Promise<T> {
  return (await unwrap<{ ok: true; data: T }>(res)).data;
}

export const api = {
  /** Narrow it where it is read (13 Oct 2026): `id`, `state` (comma list), `contact_id`, `context_ref`, `since`. Newest first. */
  communications: (c:ApiClient, q: { id?: string; state?: string; contact_id?: string; context_ref?: string; since?: string } = {}) => c.api.communications.$get({ query: q }).then(unwrapData<Record<string,unknown>[]>),
  phoneHandoffs: (c:ApiClient) => c.api["phone-handoffs"].$get().then(unwrapData<Record<string,unknown>[]>),
  /** who the session is on this surface */
  me: (c: ApiClient) => c.api.me.$get().then(unwrap<{
    user: { id: string; email: string | null };
    actor: { kind: "human"; id: string; role: string; org_id: string | null } | null;
    memberships: { role: string; org_id: string | null; status: string }[];
    surface: Surface;
    test_set_id: string | null;
  }>),

  leads: (c: ApiClient, q: { limit?: string; offset?: string; disposition?: string; location?: "active" | "all" | "attention"; channel?: string; stage_group?: string; since?: string; q?: string } = {}) =>
    c.api.leads.$get({ query: q }).then(unwrapData<Record<string, unknown>[]>),
  lead: (c: ApiClient, id: string) => c.api.leads[":id"].$get({ param: { id } }).then(unwrapData<Record<string, unknown>>),
  leadPrice: (c:ApiClient,id:string) => c.api.leads[":id"].price.$get({param:{id}}).then(unwrapData<{status:string;cost_aud:number|null;credits:number|null;observed_at?:string;reason?:string}>),
  leadTouches: (c: ApiClient, id: string) => c.api.leads[":id"].touches.$get({param:{id}}).then(unwrapData<Record<string, unknown>[]>),
  leadFunnel: (c: ApiClient, q: { since?: string; limit?: string } = {}) => c.api["lead-funnel"].$get({ query: q }).then(unwrapData<Record<string, unknown>[]>),
  jobRegister: (c:ApiClient) => c.api["job-register"].$get().then(unwrapData<Record<string,unknown>[]>),
  jobs: (c: ApiClient, q: { limit?: string; offset?: string; stage_group?: string; stage?: string; q?: string } = {}) =>
    c.api.jobs.$get({ query: q }).then(unwrapData<Record<string, unknown>[]>),
  job: (c: ApiClient, ref: string) => c.api.jobs[":ref"].$get({ param: { ref } }).then(unwrapData<Record<string, unknown>>),
  jobSkirting: (c:ApiClient,ref:string) => c.api.jobs[":ref"].skirting.$get({param:{ref}}).then(unwrapData<import('@aiw/api').SkirtingGuideView>),
  jobPayments: (c:ApiClient,ref:string) => c.api.jobs[":ref"]["payment-records"].$get({param:{ref}}).then(unwrapData<import("@aiw/api").PaymentEvidence>),
  jobInvoices: (c:ApiClient,ref:string) => c.api.jobs[":ref"].invoices.$get({param:{ref}}).then(unwrapData<import("@aiw/api").InvoiceStatus[]>),
  jobActivity: (c:ApiClient,ref:string) => c.api.jobs[":ref"].activity.$get({param:{ref}}).then(unwrapData<import("@aiw/api").ActivityRecord[]>),
  contactActivity: (c:ApiClient,id:string) => c.api.contacts[":id"].activity.$get({param:{id}}).then(unwrapData<import("@aiw/api").ActivityRecord[]>),
  calendar: (c:ApiClient,q:{from:string;to:string}) => c.api.calendar.$get({query:q}).then(unwrapData<CalendarData>),
  calendarVisits: (c:ApiClient,q:{from:string;to:string}) => c.api['calendar-visits'].$get({query:q}).then(unwrapData<import('@aiw/api').SiteVisitData>),
  siteVisits: (c:ApiClient,q:{from:string;to:string}) => c.api['site-visits'].$get({query:q}).then(unwrapData<import('@aiw/api').SiteVisitData>),
  jobInstalls: (c: ApiClient, ref: string) => c.api.jobs[":ref"].installs.$get({ param: { ref } }).then(unwrapData<Record<string, unknown>[]>),
  jobQuotes: (c: ApiClient, ref: string) => c.api.jobs[":ref"].quotes.$get({ param: { ref } }).then(unwrapData<Record<string, unknown>[]>),
  /** Who this job's invoice names. Resolved by ONE server function so the job
   *  card and any document builder cannot disagree — account first, the person
   *  only when the job's personal switch is set. */
  jobBillingParty: (c: ApiClient, ref: string) => c.api.jobs[":ref"]["billing-party"].$get({ param: { ref } }).then(unwrapData<import("@artemis-group/aiw-contracts").BillingParty>),
  contactPhotos: (c:ApiClient,id:string) => c.api.contacts[":id"].photos.$get({param:{id}}).then(unwrapData<{id:string;source:string;reference:string;category:string;caption:string;recorded_at:string|null;available:boolean}[]>),
  contactPhoto: async(c:ApiClient,id:string,photoId:string,signal?:AbortSignal) => {const r=await c.api.contacts[":id"].photos[":photoId"].$get({param:{id,photoId}},{init:{signal}});if(!r.ok)await unwrap(r);return r.blob();},
  /** The document archive. One private archive; `job` is the job door, which
   *  scopes the whole browse to one job. Nothing in the response carries a blob
   *  path, a folder or a container — storage layout never reaches the face. */
  archive: (c:ApiClient,q:{job?:string}={}) => c.api.archive.$get({query:q}).then(unwrapData<import("@aiw/api").ArchiveBrowse>),
  /** Streams one document's bytes through the API. There is no public URL. */
  archiveFile: async(c:ApiClient,id:string,signal?:AbortSignal) => {const r=await c.api.archive[":id"].file.$get({param:{id}},{init:{signal}});if(!r.ok)await unwrap(r);return r.blob();},
  contactRecords: (c: ApiClient, id: string, kind: "jobs"|"leads"|"touches"|"homes"|"quotes") => c.api.contacts[":id"].records[":kind"].$get({param:{id,kind}}).then(unwrapData<Record<string,unknown>[]>),
  contact: (c: ApiClient, id: string) => c.api.contacts[":id"].$get({param:{id}}).then(unwrapData<Record<string,unknown>|null>),
  contacts: (c: ApiClient, q: { q?: string; limit?: string } = {}) => c.api.contacts.$get({ query: q }).then(unwrapData<Record<string, unknown>[]>),
  /** Accounts are business customers — the party an invoice is addressed to.
   *  Searched by name, short name or ABN digits; filtered by category. */
  accounts: (c: ApiClient, q: { q?: string; category?: string; limit?: string } = {}) => c.api.accounts.$get({ query: q }).then(unwrapData<Record<string, unknown>[]>),
  account: (c: ApiClient, id: string) => c.api.accounts[":id"].$get({ param: { id } }).then(unwrapData<Record<string, unknown> | null>),
  /** The people at a business — the "who do I call" half of the record. */
  accountContacts: (c: ApiClient, id: string) => c.api.accounts[":id"].contacts.$get({ param: { id } }).then(unwrapData<Record<string, unknown>[]>),
  followUps: (c: ApiClient, q: { limit?: string; after_due?: string; after_id?: string } = {}) => c.api["follow-ups"].$get({ query:q }).then(unwrap<{ ok:true; data:Record<string,unknown>[]; next_cursor:{after_due:string;after_id:string}|null; total?:number; overdue_count?:number }>),
  testSets: (c: ApiClient) => c.api["test-sets"].$get().then(unwrapData<{ id: string; name: string; purpose: string | null }[]>),

  /** Every mutation is a proposal. Faces call this; they never write a table (ADR-003). */
  propose: (c: ApiClient, p: Pick<Proposal, "command" | "args" | "idempotency_key"> & { surface?: Surface; revision_of?: string; verdict?: ProposalVerdict }) =>
    c.api.propose.$post({ json: p }).then(unwrap<ProposalResult>),
  /** S9: SiteIntel files a measure — the assessment JSON as saved; the server derives
   *  the rooms and proposes `attach_measured_home` as this session (staff, installer_internal).
   *  404 `no_home` when the job's contact has no member home yet. */
  measure: (c: ApiClient, m: { job_ref: string; method: "laser" | "sketch" | "lidar"; assessment: Record<string, unknown>; contact_id?: string | null; member_id?: string | null; blob_path?: string | null }) =>
    c.api.measures.$post({ json: m }).then(unwrap<ProposalResult>),
  /** Consumers render a case and post a verdict; they never execute. */
  decide: (c: ApiClient, v: Pick<Verdict, "case_id" | "decision"> & Partial<Pick<Verdict, "edited_args" | "reason">>) =>
    c.api.decide.$post({ json: v }).then(unwrap<ProposalResult>),
  case: (c: ApiClient, id: string) => c.api.cases[":id"].$get({ param: { id } }).then(unwrap<Case>),
  actions: (c: ApiClient, q: { surface?: string; status?: string; since?: string; limit?: string } = {}) =>
    c.api.actions.$get({ query: q }).then(unwrap<{ actions: Record<string, unknown>[] }>),

  upload: async (c: ApiClient, file: File, scope = "misc") => {
    const form = new FormData();
    form.set("file", file);
    form.set("scope", scope);
    return c.api.upload.$post({ form: { file, scope } }).then(unwrap<{ path: string; url: string; size: number; content_type: string; name: string }>);
  },

  /** SSE of `case` events for this surface; the face renders CaseCard on each. */
  caseStreamUrl: (baseUrl: string, surface: Surface) => `${baseUrl.replace(/\/$/, "")}/api/cases/stream?surface=${surface}`,
};

/** A mutation as a one-liner, matching the S0 signature the faces already import. */
export async function propose(client: ApiClient, p: Pick<Proposal, "command" | "args" | "idempotency_key"> & { surface?: Surface; revision_of?: string; verdict?: ProposalVerdict }): Promise<ProposalResult> {
  return api.propose(client, p);
}
